import { ReactElement, useEffect, useState } from "react"
import {
  BsFacebook,
  BsInstagram,
  BsLinkedin,
  BsWhatsapp,
  BsYoutube,
} from "react-icons/bs"
import DropDownMenu, { navigateTo } from "./DropDownMenu"
import styled from "styled-components"
import { useTranslation } from "../TranslationProvider"
import { zIndex } from "../lib/theme"

// Custom hook to detect when BodyWrapper is visible
function useBodyWrapperVisibility(): boolean {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: "0px 0px -20% 0px", // Only trigger when element is 20% from bottom
      }
    )

    // Wait for the DOM to be ready, then find the BodyWrapper element
    const findAndObserveBodyWrapper = () => {
      const bodyWrapper = document.getElementById("body-wrapper")
      if (bodyWrapper) {
        observer.observe(bodyWrapper)
        return bodyWrapper
      }
      return null
    }

    // Try to find the element immediately
    let bodyWrapper = findAndObserveBodyWrapper()

    // If not found, wait a bit and try again (for cases where the DOM isn't ready)
    if (!bodyWrapper) {
      const timeoutId = setTimeout(() => {
        bodyWrapper = findAndObserveBodyWrapper()
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        observer.disconnect()
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  return isVisible
}

export default function Overlay(): ReactElement {
  const { locale, setLocale, t } = useTranslation()
  const isBodyWrapperVisible = useBodyWrapperVisibility()

  return (
    <OverlayWrapper className="overlay">
      <CallToActionWrapper style={{ opacity: isBodyWrapperVisible ? 1 : 0 }}>
        <CallToActionButton
          onClick={() => {
            navigateTo("#footer", "start")
          }}>
          <span>
            {t("callToAction.book")}
            <br />
            {t("callToAction.us")}
          </span>
        </CallToActionButton>
      </CallToActionWrapper>

      <TopNavBar>
        <TopNavBarLeft>
          <NavButton
            href="https://www.facebook.com/stepthegap"
            target="_blank"
            rel="noopener noreferrer">
            <BsFacebook size={30} />
          </NavButton>

          <NavButton
            href="https://wa.me/message/WTY3OGWQ6WHZJ1"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--whatsapp)" }}>
            <BsWhatsapp size={30} />
          </NavButton>

          <NavButton
            href="https://www.instagram.com/stepthegap/?hl=en"
            target="_blank"
            rel="noopener noreferrer">
            <BsInstagram size={30} />
          </NavButton>
        </TopNavBarLeft>

        <TopNavBarRight>
          <NavButtonLanguage
            onClick={() => setLocale(locale === "nl" ? "en" : "nl")}>
            <LanguageText translate="no">
              {locale === "nl" ? "EN" : "NL"}
            </LanguageText>
          </NavButtonLanguage>

          <DropDownMenu />
        </TopNavBarRight>
      </TopNavBar>

      <BottomNavBar>
        <NavButtonHideSm
          href="https://www.linkedin.com/company/stepthegap"
          target="_blank"
          rel="noopener noreferrer">
          <BsLinkedin size={30} />
        </NavButtonHideSm>

        <NavButtonHideSm
          href="https://www.facebook.com/stepthegap"
          target="_blank"
          rel="noopener noreferrer">
          <BsFacebook size={30} />
        </NavButtonHideSm>

        <NavButtonHideSmLandscape
          href="https://wa.me/message/WTY3OGWQ6WHZJ1"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#25D366" }}>
          <BsWhatsapp size={36} />
        </NavButtonHideSmLandscape>

        <NavButtonHideSmLandscape
          href="https://www.instagram.com/stepthegap/?hl=en"
          target="_blank"
          rel="noopener noreferrer">
          <BsInstagram size={30} />
        </NavButtonHideSmLandscape>

        <NavButtonHideSm
          href="https://www.youtube.com/user/stepthegap"
          target="_blank"
          rel="noopener noreferrer">
          <BsYoutube size={30} />
        </NavButtonHideSm>
      </BottomNavBar>
    </OverlayWrapper>
  )
}

const OverlayWrapper = styled.div`
  isolation: isolate;
  position: relative;
  z-index: ${zIndex.overlay};
`

const NavBar = styled.div`
  position: fixed;
  z-index: 1;

  width: 100%;
  max-width: 150rem;
  padding: 0.5rem;

  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TopNavBar = styled(NavBar)`
  top: 0;
  translate: -50%;

  justify-content: space-between;

  background-color: rgb(0, 0, 0, 0.5);
`

const TopNavBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media screen and (min-width: 500px) {
    visibility: hidden;
  }

  @media screen and (orientation: landscape) {
    visibility: hidden;
  }
`

const TopNavBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const BottomNavBar = styled(NavBar)`
  bottom: 1%;
  left: 1%;

  max-width: fit-content;

  @media screen and (min-width: 500px) and (max-width: 1200px) {
    left: 50%;
    transform: translateX(-50%);
  }
`

const NavButton = styled.a`
  padding: 0.5em;
  border-radius: 0.5rem;
  box-shadow: 0.1rem 0.1rem 0.3rem black;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: var(--primary);
  color: var(--white);

  cursor: pointer;

  :hover {
    scale: 1.1;
  }
`

const NavButtonHideSmLandscape = styled(NavButton)`
  display: none;

  @media screen and (orientation: landscape) {
    display: flex;
  }

  @media screen and (min-width: 500px) {
    display: flex;
  }
`

const NavButtonHideSm = styled(NavButton)`
  display: none;

  @media screen and (min-width: 500px) {
    display: flex;
  }
`

const NavButtonLanguage = styled(NavButton)`
  width: 46px;
  height: 46px;

  background-color: var(--white);
  color: var(--primary);

  :hover {
    background-color: var(--white);
    color: var(--primary);
  }
`

const LanguageText = styled.span`
  font-size: 1.4rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
`

const CallToActionWrapper = styled.div`
  z-index: 2;
  position: fixed;
  top: 1rem;
  left: 1rem;

  transition: opacity 0.5s ease-in-out;

  @media screen and (orientation: portrait) {
    top: auto;
    left: auto;
    bottom: 1rem;
    right: 1rem;
  }
`

const CallToActionButton = styled.div`
  @property --degree {
    syntax: "<angle>";
    initial-value: 0deg;
    inherits: false;
  }

  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;

  box-shadow: 0.1rem 0.1rem 0.3rem black;

  display: flex;
  justify-content: center;
  align-items: center;

  background: linear-gradient(45deg, rgba(0, 0, 0, 1) 50%, teal);
  font-size: 1.7rem;
  color: var(--primary);
  -webkit-text-stroke: 0.8px var(--white);

  cursor: pointer;
  user-select: none;

  span {
    transform: rotate(-12deg);
  }

  &::before,
  &::after {
    position: absolute;
    z-index: -1;
    top: 50%;
    left: 50%;
    translate: -50% -50%;

    width: calc(100% + 6px);
    height: calc(100% + 6px);
    border-radius: 50%;

    content: "";
    background-image: conic-gradient(
      from var(--degree),
      transparent 80%,
      var(--secondary)
    );

    animation: rotating-border 2s linear infinite;
  }

  &::before {
    filter: blur(1.5px);
  }

  @keyframes rotating-border {
    to {
      --degree: 360deg;
    }
  }

  span:hover {
    scale: 1.05;
  }
`
